import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Inbox, MessageSquare, Phone, FileText, Loader2, RefreshCcw, Hash, Users, BookOpen, ShoppingBag, UserPlus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { fetchAdminOverview, updateAidStatus, adminListLawyers, fetchGuides, fetchProducts } from '../lib/api';
import LawyersTab from '../components/LawyersTab';
import GuidesTab from '../components/GuidesTab';
import ProductsTab from '../components/ProductsTab';
import OrdersTab from '../components/OrdersTab';
import EnrollmentsTab from '../components/EnrollmentsTab';

const HTTP_UNAUTHORIZED = 401;
const DEFAULT_AID_STATUSES = ['Submitted', 'In Review', 'Advocate Assigned', 'Closed'];

const STATUS_COLORS = {
  'Submitted': 'bg-[#E89530]/15 text-[#A36B1F] border-[#E89530]/30',
  'In Review': 'bg-blue-100 text-blue-800 border-blue-200',
  'Advocate Assigned': 'bg-amber-100 text-amber-900 border-amber-200',
  'Closed': 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#E8DCC0] rounded-2xl p-6 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl bg-[#EDE3CC] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#0F1B2D]" strokeWidth={1.7} />
    </div>
    <div>
      <p className="text-xs uppercase tracking-wider text-[#5a5a5a]">{label}</p>
      <p className="font-serif-besley text-3xl text-[#0F1B2D] leading-none mt-1">{value}</p>
    </div>
  </div>
);

const Empty = ({ label }) => (
  <div className="text-center py-16 text-[#5a5a5a] bg-white border border-dashed border-[#E8DCC0] rounded-2xl">
    No {label} yet.
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const passcode = typeof window !== 'undefined' ? sessionStorage.getItem('lfl_admin_pass') : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [allLawyers, setAllLawyers] = useState([]);
  const [guides, setGuides] = useState([]);
  const [products, setProducts] = useState([]);

  const approvedLawyers = allLawyers.filter((l) => (l.status || 'approved') === 'approved');
  const enrollmentsPending = allLawyers.filter((l) => ['pending_review', 'rejected'].includes(l.status));
  const pendingReviewCount = allLawyers.filter((l) => l.status === 'pending_review').length;

  const AID_STATUSES = data?.aid_statuses || DEFAULT_AID_STATUSES;

  const load = useCallback(() => {
    if (!passcode) {
      navigate('/admin');
      return;
    }
    setLoading(true);
    Promise.all([
      fetchAdminOverview(passcode),
      adminListLawyers(passcode).catch(() => []),
      fetchGuides().catch(() => []),
      fetchProducts({ in_stock_only: false }).catch(() => []),
    ])
      .then(([overview, lw, gd, pd]) => {
        setData(overview);
        setAllLawyers(Array.isArray(lw) ? lw : []);
        setGuides(Array.isArray(gd) ? gd : []);
        setProducts(Array.isArray(pd) ? pd : []);
      })
      .catch((err) => {
        if (err?.response?.status === HTTP_UNAUTHORIZED) {
          sessionStorage.removeItem('lfl_admin_pass');
          toast.error('Session expired. Please sign in again.');
          navigate('/admin');
        } else {
          toast.error('Could not load admin data.');
        }
      })
      .finally(() => setLoading(false));
  }, [passcode, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    sessionStorage.removeItem('lfl_admin_pass');
    navigate('/admin');
  };

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      const updated = await updateAidStatus(passcode, id, status);
      setData((prev) => prev ? {
        ...prev,
        aid_requests: (prev.aid_requests || []).map((r) => r.id === id ? { ...r, ...updated } : r),
      } : prev);
      toast.success(`Status updated to "${status}"`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update status.');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-32 text-[#5a5a5a]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const counts = data?.counts || { aid_requests: 0, messages: 0, lawyer_contacts: 0 };

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <p className="text-xs tracking-[0.3em] text-[#E89530] mb-3">ADMIN</p>
            <h1 className="font-serif-besley text-4xl md:text-5xl">Dashboard</h1>
            <p className="text-[#C9BFA8] mt-2">Live overview of incoming requests and messages.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#E8DCC0]/30 text-sm hover:border-[#E89530] hover:text-[#E89530] transition-colors"
            >
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#E89530] text-[#0F1B2D] text-sm font-medium hover:bg-[#F2A547] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-10">
            <StatCard icon={FileText} label="Free aid" value={counts.aid_requests} />
            <StatCard icon={MessageSquare} label="Messages" value={counts.messages} />
            <StatCard icon={Phone} label="Adv. contacts" value={counts.lawyer_contacts} />
            <StatCard icon={Users} label="Lawyers" value={approvedLawyers.length} />
            <StatCard icon={UserPlus} label="Enrollments" value={pendingReviewCount} />
            <StatCard icon={BookOpen} label="Guides" value={guides.length} />
            <StatCard icon={ShoppingBag} label="Products" value={products.length} />
          </div>

          <Tabs defaultValue="aid" className="w-full">
            <TabsList className="bg-[#EDE3CC] border border-[#E8DCC0] mb-6 flex flex-wrap h-auto">
              <TabsTrigger value="aid" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Free aid ({counts.aid_requests})
              </TabsTrigger>
              <TabsTrigger value="messages" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Messages ({counts.messages})
              </TabsTrigger>
              <TabsTrigger value="contacts" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Advocate contacts ({counts.lawyer_contacts})
              </TabsTrigger>
              <TabsTrigger value="lawyers" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Lawyers ({approvedLawyers.length})
              </TabsTrigger>
              <TabsTrigger data-testid="enrollments-tab-trigger" value="enrollments" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Enrollments ({pendingReviewCount})
              </TabsTrigger>
              <TabsTrigger value="guides" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Legal guides ({guides.length})
              </TabsTrigger>
              <TabsTrigger value="products" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Products ({products.length})
              </TabsTrigger>
              <TabsTrigger value="orders" className="data-[state=active]:bg-white data-[state=active]:text-[#0F1B2D]">
                Orders
              </TabsTrigger>
            </TabsList>

            <TabsContent value="aid">
              {!data?.aid_requests?.length ? (
                <Empty label="aid requests" />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.aid_requests.map((r) => {
                    const status = r.status || 'Submitted';
                    const isUpdating = updatingId === r.id;
                    return (
                      <article key={r.id} className="bg-white border border-[#E8DCC0] rounded-2xl p-6">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <h3 className="font-serif-besley text-xl text-[#0F1B2D] truncate">{r.fullName}</h3>
                            <p className="text-sm text-[#5a5a5a]">{r.phone} {r.state ? `\u00b7 ${r.state}` : ''}</p>
                            {r.tracking_number && (
                              <p className="text-xs mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#EDE3CC] text-[#0F1B2D] font-mono">
                                <Hash className="w-3 h-3" />{r.tracking_number}
                              </p>
                            )}
                          </div>
                          <Badge className="bg-[#E89530]/15 text-[#A36B1F] border border-[#E89530]/30 shrink-0">{r.matter}</Badge>
                        </div>
                        {r.description ? (
                          <p className="text-sm text-[#3a3a3a] leading-relaxed mb-3 whitespace-pre-wrap">{r.description}</p>
                        ) : null}
                        <div className="flex items-center justify-between gap-3 mt-3">
                          <p className="text-xs text-[#5a5a5a]"><Inbox className="inline w-3 h-3 mr-1" />{formatDate(r.updatedAt || r.createdAt)}</p>
                          <div className="flex items-center gap-2">
                            <span className={`hidden md:inline text-[11px] px-2 py-1 rounded border ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                              {status}
                            </span>
                            <Select
                              value={status}
                              onValueChange={(v) => handleStatusChange(r.id, v)}
                              disabled={isUpdating}
                            >
                              <SelectTrigger className="h-9 w-44 text-sm border-[#E8DCC0]">
                                <SelectValue placeholder="Update status" />
                              </SelectTrigger>
                              <SelectContent>
                                {AID_STATUSES.map((s) => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="messages">
              {!data?.messages?.length ? (
                <Empty label="messages" />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.messages.map((m) => (
                    <article key={m.id} className="bg-white border border-[#E8DCC0] rounded-2xl p-6">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h3 className="font-serif-besley text-xl text-[#0F1B2D]">{m.name}</h3>
                          <a className="text-sm text-[#A36B1F] hover:underline" href={`mailto:${m.email}`}>{m.email}</a>
                        </div>
                      </div>
                      <p className="text-sm text-[#3a3a3a] leading-relaxed mb-3 whitespace-pre-wrap">{m.message}</p>
                      <p className="text-xs text-[#5a5a5a]"><Inbox className="inline w-3 h-3 mr-1" />{formatDate(m.createdAt)}</p>
                    </article>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="contacts">
              {!data?.lawyer_contacts?.length ? (
                <Empty label="advocate contacts" />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.lawyer_contacts.map((c) => (
                    <article key={c.id} className="bg-white border border-[#E8DCC0] rounded-2xl p-6">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h3 className="font-serif-besley text-xl text-[#0F1B2D]">{c.lawyer_name || 'Advocate'}</h3>
                          <p className="text-sm text-[#5a5a5a]">Requested by {c.name || 'Anonymous'}{c.phone ? ` \u00b7 ${c.phone}` : ''}</p>
                        </div>
                      </div>
                      {c.note ? (
                        <p className="text-sm text-[#3a3a3a] leading-relaxed mb-3 whitespace-pre-wrap">{c.note}</p>
                      ) : null}
                      <p className="text-xs text-[#5a5a5a]"><Inbox className="inline w-3 h-3 mr-1" />{formatDate(c.createdAt)}</p>
                    </article>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="lawyers">
              <LawyersTab
                passcode={passcode}
                lawyers={approvedLawyers}
                onChange={(nextApproved) => {
                  const others = allLawyers.filter((l) => (l.status || 'approved') !== 'approved');
                  setAllLawyers([...nextApproved, ...others]);
                }}
                onRefetch={load}
              />
            </TabsContent>

            <TabsContent value="enrollments">
              <EnrollmentsTab
                passcode={passcode}
                enrollments={enrollmentsPending}
                onChange={(nextEnrollments) => {
                  const merged = [...allLawyers];
                  nextEnrollments.forEach((n) => {
                    const idx = merged.findIndex((m) => m.id === n.id);
                    if (idx >= 0) merged[idx] = { ...merged[idx], ...n };
                  });
                  setAllLawyers(merged);
                }}
              />
            </TabsContent>

            <TabsContent value="guides">
              <GuidesTab passcode={passcode} guides={guides} onChange={setGuides} />
            </TabsContent>

            <TabsContent value="products">
              <ProductsTab passcode={passcode} products={products} onChange={setProducts} />
            </TabsContent>

            <TabsContent value="orders">
              <OrdersTab passcode={passcode} />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
